import {
    Dispatch,
    forwardRef,
    KeyboardEvent,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'
import style from './style.m.less'
import { useTranslation } from 'react-i18next'

import { Form, Input, Typography, message, Checkbox } from 'antd'
import { FormHandle, PollutantsResultResponse } from '../../types'
import { formatNumber, restrictInput } from '../../helpers'
import { ButtonsBlock } from '../index'
import { PollutantsTable } from './PollutantsTable'

const { Text } = Typography

interface IThermalForm {
    error: string
    resCalculate: PollutantsResultResponse | null
    // auditId: number
    // isLoadingAuditUpdate: boolean
    // resCalculatorAudit: CalculatorAuditResponse

    setError: Dispatch<SetStateAction<string>>
    scrollToMain: () => void
    fetchCalculate: (body: any) => void
    // resetAudit: () => void
    // auditUpdate: (auditData: CalculatorAuditValues) => void
    // updateAuditId: (values: any, onSuccess: (values: any, data: any) => void) => void
}

const PollutantsForm = forwardRef<FormHandle, IThermalForm>(
    ({ error, resCalculate, setError, scrollToMain, fetchCalculate }, ref) => {
        const { t, i18n } = useTranslation()
        const locale = i18n.language.includes('ru') ? 'ru' : 'en'

        const [calcForm] = Form.useForm()
        const [result, setResult] = useState<PollutantsResultResponse | null>(null)
        const [volume, setVolume] = useState<string | null>(null)

        useEffect(() => {
            if (resCalculate !== null) {
                setResult(resCalculate)

                setTimeout(() => {
                    document
                        .getElementById('result_container')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 150)
            }
        }, [resCalculate])

        const onFinish = async (values: any) => {
            setError('')
            setVolume(values.volume)
            try {
                const requestData = {
                    volume: values.volume.replaceAll(' ', '').replace(',', '.'),
                    locale: locale,
                }
                fetchCalculate(requestData)
            } catch (err) {
                setError(t('calculator.errors.calculation'))
                message.error(t('calculator.errors.calculation'))
            }
        }

        const resetForm = () => {
            calcForm.resetFields(['volume'])
            setResult(null)
            setTimeout(() => {
                scrollToMain()
            }, 200)
        }

        useImperativeHandle(ref, () => ({
            resetForm,
        }))

        const formatVolume = (value: string) => {
            if (!value || value === '') {
                return
            }
            if (value[value.length - 1] === '.' || value[value.length - 1] === ',') {
                return
            }

            const formatted = formatNumber(value)

            calcForm.setFieldsValue({ volume: formatted })
        }

        const restrictVolumeInput = (event: KeyboardEvent<HTMLInputElement>) => {
            restrictInput(event)
        }

        return (
            <>
                <Form
                    form={calcForm}
                    layout="vertical"
                    onFinish={onFinish}
                    className={style.formWrap}
                >
                    <div className={style.calculatorFields}>
                        <div className={style.formDescription}>
                            {t('calculator.pollutantsDescription')}
                        </div>
                        <a href="/documents/pollutantsMethod.pdf" download target="_blank">
                            {t('calculator.pollutantsFile')}
                        </a>
                        {/* Поле объема */}
                        <Form.Item
                            label={t('calculator.fields.volume.label')}
                            className={style.formItem}
                            name="volume"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.fields.volume.error.required'),
                                },
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.reject()
                                        }
                                        const normalizedValue = value.replaceAll(' ', '')
                                        return /^\d+([,.]\d+)?$/.test(normalizedValue)
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                  t('calculator.fields.volume.error.format'),
                                              )
                                    },
                                },
                            ]}
                        >
                            <Input
                                onKeyDown={restrictVolumeInput}
                                onChange={(e) => formatVolume(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="agreement"
                            valuePropName="checked"
                            rules={[
                                {
                                    required: true,
                                    message: t('calculator.pollutantsAgreementRequired'),
                                },
                                {
                                    validator: (_, value) => {
                                        if (value == false) {
                                            return Promise.reject(
                                                t('calculator.pollutantsAgreementRequired'),
                                            )
                                        }
                                        return Promise.resolve()
                                    },
                                },
                            ]}
                        >
                            <div className={style.formCheckobx}>
                                <Checkbox />
                                <label>
                                    {t('calculator.pollutantsAgreementText')}
                                    <a
                                        className={style.agreeLink}
                                        href="/documents/pollutantsAgreement.pdf"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {t('calculator.pollutantsAgreementFile')}
                                    </a>
                                    <label className={style.star}>*</label>
                                </label>
                            </div>
                        </Form.Item>
                        <Form.Item>
                            <ButtonsBlock
                                margin
                                confirmTitle="Рассчитать"
                                cancelAction={() => resetForm()}
                            />
                        </Form.Item>
                    </div>
                    {error && <Text type="danger">{error}</Text>}
                </Form>
                {!!result && !!volume && <PollutantsTable result={result} volume={volume} />}
            </>
        )
    },
)
export default PollutantsForm
